import React, { useRef, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface MyHorizontalScrollableProps {
  children: React.ReactNode;
  className?: string;
  itemGap?: string;
  showButtons?: boolean;

  // optional
  desktopWrap?: boolean;
  scrollAmount?: number;
}

export const MyHorizontalScrollable: React.FC<
  MyHorizontalScrollableProps
> = ({
  children,
  className = "",
  itemGap = "gap-3",
  showButtons = true,
  desktopWrap = false,
  scrollAmount = 320,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const updateScrollState = () => {
    const el = containerRef.current;
    if (!el) return;

    const overflowing = el.scrollWidth > el.clientWidth + 2;

    setIsOverflowing(overflowing);
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(
      el.scrollLeft + el.clientWidth < el.scrollWidth - 2
    );
  };

  useEffect(() => {
    updateScrollState();

    const el = containerRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateScrollState);

    const resizeObserver = new ResizeObserver(() => {
      updateScrollState();
    });

    resizeObserver.observe(el);

    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);

  const scrollByAmount = (amount: number) => {
    containerRef.current?.scrollBy({
      left: amount,
      behavior: "smooth",
    });
  };

  return (
    <div className={`relative w-full ${className}`}>
      {/* LEFT BUTTON */}
      {showButtons && isOverflowing && canScrollLeft && (
        <button
          onClick={() => scrollByAmount(-scrollAmount)}
          className="
            absolute left-2 top-1/2 -translate-y-1/2 z-20
            bg-white/90 dark:bg-gray-800/90
            backdrop-blur
            shadow-lg rounded-full p-2
            hover:scale-105 transition
          "
        >
          <ChevronLeft size={18} />
        </button>
      )}

      {/* RIGHT BUTTON */}
      {showButtons && isOverflowing && canScrollRight && (
        <button
          onClick={() => scrollByAmount(scrollAmount)}
          className="
            absolute right-2 top-1/2 -translate-y-1/2 z-20
            bg-white/90 dark:bg-gray-800/90
            backdrop-blur
            shadow-lg rounded-full p-2
            hover:scale-105 transition
          "
        >
          <ChevronRight size={18} />
        </button>
      )}

      {/* SCROLL AREA */}
      <div
        ref={containerRef}
        className={`
          w-full
          flex ${itemGap}

          overflow-x-auto
          scroll-smooth
          scrollbar-hide

          px-1 py-1

          ${
            desktopWrap
              ? "lg:flex-wrap lg:overflow-visible"
              : ""
          }

          cursor-grab active:cursor-grabbing
        `}
      >
        {children}
      </div>
    </div>
  );
};